/**
 * Central-accounts token top-up deep-link.
 *
 * Owner mandate: token PURCHASE (Stripe) lives ONLY in the accounts app — no
 * portal-local Stripe key path for top-ups. Every portal "Buy tokens" entry
 * point routes here, to accounts `/billing`, carrying:
 *   - `app=portal`  → accounts validates the return against portal's
 *                      allowedReturnOrigins (SSO open-redirect guard).
 *   - `return`      → the caller's CURRENT url, so after a successful checkout
 *                      accounts bounces the user straight back to where they were.
 *   - `lang`        → accounts renders in the same locale.
 *
 * Client-only: reads `window.location.href` at CALL time (so it captures the
 * exact deep-link the user is on). Pass `locale` from next-intl's useLocale().
 */
import { openSubscribe, isSubscribeWidgetMounted } from '@owlcat/app-shell';
import { openExternalUrl } from '@/shared/utils/openExternal';

const stripSlash = (u: string) => u.replace(/\/+$/, '');

/** Browser-reachable accounts origin (NEXT_PUBLIC_ACCOUNTS_URL; dev loopback). */
function publicAccountsUrl(): string {
  return stripSlash(process.env.NEXT_PUBLIC_ACCOUNTS_URL || 'http://127.0.0.1:3200');
}

/**
 * Central-accounts SUBSCRIPTION deep-link — the plans + manage surface
 * (`/billing/activity`: lists subscription_plans + SubscribeButton + the user's
 * live subscriptions), NOT the token top-up page. Every "Subscribe" / "See plans"
 * / "Manage subscription" CTA routes here. Same `app=portal` + `return` SSO
 * handshake + `lang` as {@link accountsTopUpUrl}. Client-only — pass `locale`.
 */
export function accountsSubscribeUrl(locale: string): string {
  const q = new URLSearchParams({ lang: locale, app: 'portal' });
  if (typeof window !== 'undefined') q.set('return', window.location.href);
  return `${publicAccountsUrl()}/billing/activity?${q.toString()}`;
}

/**
 * "See plans" CTA → the subscription surface (alias of {@link accountsSubscribeUrl}).
 * Kept for existing call sites; distinct name reads as "go see subscription plans".
 */
export function accountsBillingUrl(locale: string): string {
  return accountsSubscribeUrl(locale);
}

/**
 * THE subscribe/manage entry point for every portal CTA (header meter, profile
 * menu, allowance wall, wizard rate-limit nudge): opens the shared in-app
 * <SubscribeWidget> (mounted in the root layout — plans + top-up, Stars in the
 * Mini App, embedded Stripe in the browser, no navigation away). Only when the
 * widget is not mounted does it fall back to the accounts billing page.
 */
export function openSubscription(locale: string, webApp?: import('@/shared/utils/openExternal').TelegramWebAppLike | null): void {
  if (isSubscribeWidgetMounted()) {
    openSubscribe('portal');
    return;
  }
  openExternalUrl(accountsSubscribeUrl(locale), webApp);
}
